import { useBunja } from 'bunja/react';
import { isNil } from 'es-toolkit';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { CompetitionStatusEnum, RankByEnum, RankingStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { FormattedMessage } from '@flipster/i18n';
import { Link, useRouter } from '@flipster/i18n/routing';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { OrdinalNumber } from '@flipster/react/design-utils/ordinal-number';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { Styles, css } from '@flipster/styled-system/css';
import { divider, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import { UsdPrecision, UsdtCurrency } from '@flipster/utils/constants/currency';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { useAuth } from '../shared/auth/state';
import { useGetUrlWithAnonymousId } from '../shared/hooks/useGetUrlWithAnonymousId';
import { FlipsterIcon } from '../shared/logo';
import { useIsWebview } from '../shared/webview';
import { competitionBunja, competitionRankingBunja } from './bunja';

const RankingByText = {
  [RankByEnum.Pnl]: 'P&L (USDT)',
  [RankByEnum.Volume]: 'Volume (USDT)',
};

export const MyInfo = ({ css: cssProps }: { css?: Styles }) => {
  const t = useTranslations();
  const { id, competitionAtom, deregisterCompetition } = useBunja(competitionBunja);
  const { isLessThanAwardListAtom, myRankingAtom } = useBunja(competitionRankingBunja);
  const isLessThanAwardList = useAtomValue(isLessThanAwardListAtom);
  const myRanking = useAtomValue(myRankingAtom);

  const competition = useAtomValue(competitionAtom);
  const { user } = useAuth();
  const { isDesktop } = useBreakpoint();
  const isWebview = useIsWebview();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();
  const router = useRouter();
  const onClickTradeNow = () => {
    segment.track(CompetitionsEvent.CompetitionRankingPageTradeNowClick, { competition_id: id });

    if (isDesktop || isWebview) {
      webviewHandler.postMessage(
        {
          action: 'link',
          params: {
            page: 'order/latest',
          },
        },
        () => router.push('/trade/latest'),
      );
    } else {
      alert.show({
        message: (
          <div className={vstack({ gap: 12 })}>
            <FlipsterIcon size='small' type='rounded' />
            <div className={vstack({ gap: 4 })}>
              <p
                className={css({
                  textStyle: 'Title/S',
                  color: 'content.default.level-5',
                  textAlign: 'center',
                })}
              >
                <FormattedMessage id='Autogen_common.r251126.s_Start_trading_on_Flipster' />
              </p>
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-3',
                  textAlign: 'center',
                })}
              >
                <FormattedMessage id='Autogen_common.r251126.s_Install_the_Flipster_app_to_start_trading_and_climb_the_leaderboard' />
              </p>
            </div>
          </div>
        ),
        confirmText: 'Download app',
        onConfirm: () => {
          window.open(getUrlWithAnonymousId('Download'), '_blank', 'noopener');
        },
        closeText: 'Not now',
        withClose: true,
      });
    }
  };

  const onClickCancelRegistration = () => {
    alert.show({
      message: 'Are you sure you want to cancel your participation?',
      confirmText: 'Yes',
      async onConfirm() {
        try {
          await deregisterCompetition();
        } catch (err) {
          const errorCode = getErrorCode(err);
          const errorMessage = getErrorMessage(err);
          if (errorCode === 'api.unauthorized') {
            alert.show({
              message: errorMessage,
              onConfirm() {
                webviewHandler.postMessage({ action: 'link', params: { page: 'login' } });
              },
            });
          } else alert.error(err);
        }
      },
      closeText: 'Cancel',
      withClose: true,
    });
  };
  if (!competition) return null;

  const isUnfulfilled =
    competition?.competitionType != 'WEEKLY_PNL' && // recurring tc
    myRanking?.rankingStatus === RankingStatusEnum.Unfulfilled;

  const isPnl = competition.rankBy === RankByEnum.Pnl;
  return (
    <div
      className={css(
        {
          m: { base: '30px 16px', tablet: '40px auto 60px' },
          tablet: { maxW: 'desktopMaxWidth', px: 16, boxSizing: 'content-box' },
        },
        cssProps,
      )}
    >
      <div
        className={stack({
          p: {
            base: 16,
            tablet: 24,
          },
          rounded: 8,
          gap: 24,
          flexDirection: {
            base: 'column',
            tablet: 'row',
          },
          justifyContent: {
            base: 'center',
            tablet: 'space-between',
          },
          w: '100%',
          bg: 'background.default.level-1',
        })}
      >
        <div className={stack({ gap: 12 })}>
          <Link href='/user/profile' className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-3' })}>
            {user?.nickname}
          </Link>
          <div className={hstack({ gap: 16, alignItems: 'stretch' })}>
            <div
              className={hstack({
                gap: { base: 4, tablet: 8 },
                tabletDown: { flexDirection: 'column', alignItems: 'flex-start' },
              })}
            >
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
                <FormattedMessage id='Autogen_common.r251126.s_Ranking' />
              </p>
              {isLessThanAwardList || isUnfulfilled || myRanking?.rank == undefined ? (
                <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>
                  <FormattedMessage id='Autogen_common.r251126.s_Unranked' />
                </p>
              ) : (
                <OrdinalNumber max={3} value={myRanking.rank} css={{ textStyle: 'Title/S', color: 'inverse.white' }} />
              )}
            </div>
            <hr
              className={divider({
                orientation: 'vertical',
                borderColor: 'background.default.level-4',
                w: 1,
                h: 'inherit',
              })}
            />
            <div
              className={hstack({
                gap: { base: 4, tablet: 8 },
                tabletDown: { flexDirection: 'column', alignItems: 'flex-start' },
              })}
            >
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-1',
                })}
              >
                {RankingByText[competition.rankBy]}
              </p>
              <Formatted.Number
                value={isPnl ? myRanking?.pnl : myRanking?.volume}
                css={{ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' }}
                zeroString='-'
                suffix={isPnl && !isNil(myRanking?.pnl) ? UsdtCurrency : undefined}
                precision={UsdPrecision}
                withColor={isPnl}
                withPlus={isPnl}
              />
            </div>
          </div>
          {isUnfulfilled && (
            <div className={hstack({ gap: 4 })}>
              <Badge type='tint' color='warning'>
                <FormattedMessage id='Autogen_common.r251126.s_Not_eligible' />
              </Badge>
              <p
                className={css({
                  textStyle: 'Text/S/Regular',
                  color: 'content.state.warning',
                })}
              >
                <FormattedMessage id='Autogen_common.r251126.s_Minimum_trading_volume_not_met_for_prize' />
              </p>
            </div>
          )}
        </div>
        <div className={vstack({ gap: 16, tablet: { flexDirection: 'row-reverse' } })}>
          <Button
            css={{ minW: { base: '100%', tablet: 'fit-content' } }}
            hierarchy='primary'
            color='accent'
            size='medium'
            label={t('Autogen_common.r251126.s_Trade_now')}
            onClick={onClickTradeNow}
          />
          {competition.competitionType != 'WEEKLY_PNL' && // recurring tc
            competition.competitionStatus !== CompetitionStatusEnum.Finished && (
              <TextButton
                size='small'
                label={t('Autogen_common.r251126.s_Cancel_registration')}
                color='passive'
                css={{
                  minW: 'fit-content',
                }}
                onClick={onClickCancelRegistration}
              />
            )}
        </div>
      </div>
    </div>
  );
};
